'use client'

import { useRouter } from 'next/navigation'

/**
 * Nettoie le nom d'artiste avant de le passer dans l'URL :
 *  - retire " - Topic" (suffixe auto-généré YouTube Music)
 *  - retire "VEVO"
 *  - retire "Official Music Channel" / "Official Channel"
 */
export function cleanArtistForUrl(artist: string): string {
  return artist
    .replace(/\s*-\s*Topic\s*$/i, '')
    .replace(/\s+VEVO\s*$/i, '')
    .replace(/\s*Official(\s+Music)?(\s+Channel)?\s*$/i, '')
    .trim()
}

/**
 * Lien cliquable vers la fiche artiste `/app/artist/[name]`.
 *
 * ⚠️ N'utilise PAS `<Link>` car ce composant est souvent rendu DANS un
 * `<Link>` ou `<button>` parent (PlayerBar, cartes de tracks, etc.) → un
 * <a> imbriqué dans un <a> est du HTML invalide qui plante l'hydration.
 *
 * On utilise donc un `<span role="link">` qui appelle `router.push` au click,
 * avec `stopPropagation` pour ne pas déclencher l'action du parent (ex: jouer
 * le morceau quand on clique sur l'artiste).
 *
 * Si `children` n'est pas fourni, on affiche le nom nettoyé.
 */
export default function ArtistLink({
  artist,
  className = '',
  children,
  underline = true,
}: {
  artist: string | undefined | null
  className?: string
  children?: React.ReactNode
  /** Active le underline au hover (true par défaut) */
  underline?: boolean
}) {
  const router = useRouter()
  const clean = artist ? cleanArtistForUrl(artist) : ''
  if (!clean) {
    return <span className={className}>{children || artist || ''}</span>
  }
  const go = () => router.push(`/app/artist/${encodeURIComponent(clean)}`)
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        go()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation()
          e.preventDefault()
          go()
        }
      }}
      className={`cursor-pointer hover:text-neon-cyan ${underline ? 'hover:underline' : ''} transition ${className}`}
      title={`Voir la fiche de ${clean}`}
    >
      {children || clean}
    </span>
  )
}
